// Angular
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';

// Angular Material
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import {
	MatDatepickerModule,
	DateRange,
	DefaultMatCalendarRangeStrategy,
	MatRangeDateSelectionModel,
	
} from '@angular/material/datepicker';
import { MatTooltipModule } from '@angular/material/tooltip';

// Utilities
import {
	format,
	startOfDay,
	endOfDay,
	addHours,
	parse,
	isValid
} from 'date-fns';
import { split } from 'lodash-es';

// Models
import { AppCleanup } from '../../models';
import { UtilityHelper } from '../../helpers';

@Component({
	selector: 'date-time-range-picker',
	templateUrl: './date-time-range-picker.component.html',
	styleUrl: './date-time-range-picker.component.scss',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatButtonModule,
		MatIconModule,
		MatMenuModule,
		MatDatepickerModule,
		MatTooltipModule,
		MatDatepickerModule,
		// SharedModule,
	],
	providers: [DefaultMatCalendarRangeStrategy, MatRangeDateSelectionModel],
})
export class DateTimeRangePickerComponent extends AppCleanup
	implements OnInit, OnDestroy, OnChanges {
	private readonly DATE_DISPLAY_FORMAT: string = 'M/d/yyyy HH:mm';
	
	selectedDateRange: DateRange<Date> | null = null; // For calendar binding (display dates)
	selectedDateRangeUTC: DateRangeModel | null = null; // Internal UTC storage
	selectedDateRangeCopy: DateRangeModel | null = null;
	selectedDateRangeString: string;
	startTimeFormControl: FormControl;
	endTimeFormControl: FormControl;
	dateRangeError: string = null;
	startDateString: string = '';
	startTimeString: string = '';
	endDateString: string = '';
	endTimeString: string = '';
	
	@ViewChild('menuTrigger') menuTrigger!: MatMenuTrigger;
	
	@Input() selectedDateRangeISO: DateRangeISOModel; // ISO UTC datestrings
	@Input() minDateISO: string; // ISO UTC datestring
	@Input() maxDateISO: string; // ISO UTC datestring
	@Input() time: boolean;
	@Input() timezoneOffset: number = 0;
	
	@Output() onValueChange = new EventEmitter<DateRangeISOModel>();
	
	constructor(
		private readonly selectionModel: MatRangeDateSelectionModel<Date>,
		private readonly selectionStrategy: DefaultMatCalendarRangeStrategy<Date>,
	) {
		super();
	}
	
	ngOnInit() {
		// Ensure form controls are always initialized, addresses issue with error message display.
		if (!this.startTimeFormControl) {
			this.startTimeFormControl = new FormControl<string | null>(null);
		}
		if (!this.endTimeFormControl) {
			this.endTimeFormControl = new FormControl<string | null>(null);
		}
	}
	
	ngOnDestroy() {
		super.ngOnDestroy();
	}
	
	ngOnChanges(changes: SimpleChanges) {
		if (changes.time) {
			this.startTimeFormControl = new FormControl<string | null>(null);
			this.endTimeFormControl = new FormControl<string | null>(null);
			this.clearDateRangeFields();
		}
		
		if (changes.selectedDateRangeISO) {
			if (this.selectedDateRangeISO?.start && this.selectedDateRangeISO?.end) {
				const { start, end } = this.selectedDateRangeISO;
				
				// Convert ISO strings to Date objects for internal use
				this.selectedDateRangeUTC = {
					start: new Date(start),
					end: new Date(end),
				};
				this.selectedDateRangeCopy = UtilityHelper.deepCopy(this.selectedDateRangeUTC);
				this.setDateRange(this.selectedDateRangeUTC);
			} else {
				this.selectedDateRange = null;
				this.selectedDateRangeUTC = null;
				this.selectedDateRangeCopy = null;
			}
		}
	}
	
	get areStartAndEndDatesSet(): boolean {
		return Boolean(this.startDateString && this.startTimeString && this.endDateString && this.endTimeString);
	}
	
	get displayMinDate(): Date | null {
		// For calendar boundaries, use UTC dates directly - no adjustment needed
		// The calendar will display them in browser timezone which is what we want
		return this.minDateISO ? new Date(this.minDateISO) : null;
	}
	
	get displayMaxDate(): Date | null {
		// For calendar boundaries, use UTC dates directly - no adjustment needed
		// The calendar will display them in browser timezone which is what we want
		return this.maxDateISO ? new Date(this.maxDateISO) : null;
	}
	
	get shouldDisableApplyButton(): boolean {
		return !this.selectedDateRangeUTC ||
			!!this.dateRangeError ||
			!!this.startTimeFormControl?.errors ||
			!!this.endTimeFormControl?.errors;
	}
	
	setDateRange(dateRange: DateRangeModel) {
		if (!dateRange) {
			this.selectedDateRangeString = null;
			this.selectedDateRange = null;
			this.selectedDateRangeUTC = null;

			if (this.time) {
				this.setTimePickers(null);
			}

			return;
		}

		const formattedStartDate = this.getFormattedDate(dateRange.start);
		const formattedEndDate = this.getFormattedDate(dateRange.end);
		this.selectedDateRangeString = `${formattedStartDate} - ${formattedEndDate}`;

		if (this.time) {
			this.setTimePickers(dateRange);
		}
	}
	
	rangeChanged(selectedDate: Date) {
		const selection = this.selectionModel.selection;
		const newSelection =
			this.selectionStrategy.selectionFinished(selectedDate, selection);
		
		this.selectionModel.updateSelection(newSelection, this);
		
		let utcStart: Date = null;
		let utcEnd: Date = null;
		
		if (newSelection.start) {
			utcStart = this.convertCalendarDateToUTC(newSelection.start, true);
		}
		
		if (newSelection.end) {
			utcEnd = this.convertCalendarDateToUTC(newSelection.end);
		}
		
		// Only update if we have valid dates.
		if (utcStart && utcEnd) {
			this.selectedDateRangeUTC = {
				start: utcStart,
				end: utcEnd,
			};
			
			this.checkRangeValidity();
			this.setTimePickers(this.selectedDateRangeUTC);
		}
	}
	
	setTimePickers(dateRange: DateRangeModel) {
		if (!dateRange) {
			this.clearDateRangeFields();
			return;
		}
		
		// Only proceed if we have BOTH start and end dates.
		if (!dateRange.start ||
			!dateRange.end ||
			isNaN(dateRange.start.getTime()) ||
			isNaN(dateRange.end.getTime())
		) return;
		
		// Update calendar selection FIRST with dates that will appear correct
		// in browser timezone. Add offset to UTC dates so they display as the
		// correct job-local dates.
		const calendarStart: Date = this.getTimezoneOffset(dateRange.start);
		const calendarEnd: Date = this.getTimezoneOffset(dateRange.end);
		
		// Update the calendar's selected range by setting the property it's bound to
		this.selectedDateRange = new DateRange<Date>(calendarStart, calendarEnd);
		
		// Then update form controls
		const startDateTimeString: string =
			this.getFormattedDate(this.getTimezoneOffset(dateRange.start));
		const endDateTimeString: string =
			this.getFormattedDate(this.getTimezoneOffset(dateRange.end));
		this.setDateRangeFields(startDateTimeString, endDateTimeString);
	}
	
	submit() {
		if (this.selectedDateRangeUTC?.start && this.selectedDateRangeUTC?.end) {
			let formattedStartDate = this.getFormattedDate(this.selectedDateRangeUTC.start);
			let formattedEndDate = this.getFormattedDate(this.selectedDateRangeUTC.end);

			this.selectedDateRangeString = `${formattedStartDate} - ${formattedEndDate}`;
			this.selectedDateRangeCopy = UtilityHelper.deepCopy(this.selectedDateRangeUTC);

			// Emit ISO UTC datestrings
			const isoRange: DateRangeISOModel = {
				start: this.selectedDateRangeUTC.start.toISOString(),
				end: this.selectedDateRangeUTC.end.toISOString()
			};
			this.onValueChange.emit(isoRange);
		}

		this.menuTrigger.closeMenu();
	}
	
	close() {
		if (this.selectedDateRangeCopy) {
			this.selectedDateRangeUTC =
				UtilityHelper.deepCopy(this.selectedDateRangeCopy);
			this.setDateRange(this.selectedDateRangeUTC);
		}
		this.menuTrigger.closeMenu();
	}
	
	onDateTimeInputBlur(field: 'start' | 'end') {
		this.processDateTimeInput(field);
	}
	
	onDateTimeInputEnter(field: 'start' | 'end') {
		this.processDateTimeInput(field);
	}
	
	onMenuContainerKeydown(event: KeyboardEvent) {
		if (event.key === 'Tab') {
			// Let the default TAB behavior continue (don't preventDefault)
			// but stop it from bubbling up to whatever is closing the menu.
			event.stopPropagation();
		}
	}
	
	validateEntry(field: 'start' | 'end') {
		const control = field === 'start'
			? this.startTimeFormControl
			: this.endTimeFormControl;
		const inputValue = control.value.trim();
		
		// Test parse the input as a date in M/D/YYYY HH:mm format.
		if (!this.parseDateTime(inputValue.trim())) {
			// Invalid date format - set error on the control
			control.setErrors({ invalidFormat: true });
			control.markAsTouched();
		}
	}
	
	private clearDateRangeFields(): void {
		if (this.startTimeFormControl)
			this.startTimeFormControl.setValue(null);
		if (this.endTimeFormControl)
			this.endTimeFormControl.setValue(null);
		
		this.startDateString = '';
		this.startTimeString = '';
		this.endDateString = '';
		this.endTimeString = '';
	}
	
	private setDateRangeFields(startDateTime: string, endDateTime: string): void {
		if (this.startTimeFormControl)
			this.startTimeFormControl.setValue(startDateTime);
		if (this.endTimeFormControl)
			this.endTimeFormControl.setValue(endDateTime);
		
		const start: string[] = split(startDateTime, ' ');
		const end: string[] = split(endDateTime, ' ');
		this.startDateString = start[0];
		this.startTimeString = start[1];
		this.endDateString = end[0];
		this.endTimeString = end[1];
	}
	
	private processDateTimeInput(field: 'start' | 'end') {
		const control = field === 'start'
			? this.startTimeFormControl
			: this.endTimeFormControl;
		const inputValue: string = control.value.trim();
		const parsedLocalDate: Date = this.parseDateTime(inputValue.trim());
		
		if (parsedLocalDate) {
			let utcDate: Date = addHours(parsedLocalDate, this.timezoneOffset);
			
			// Adjust if start/end date is outside of bounds.
			utcDate = this.getAdjustedEnteredDateTimesForMinMax(control, field, utcDate);
			
			// Check if there were any changes.
			if (utcDate && this.selectedDateRangeUTC[field] &&
				this.selectedDateRangeUTC[field].getTime() === utcDate.getTime()) {
				return;
			}
			
			// Update the top date-display-content area within the main control.
			this.selectedDateRangeUTC[field] = utcDate;
			
			// Refresh the input to show properly formatted value.
			this.setTimePickers(this.selectedDateRangeUTC);
			
			// Validate the date range, after the updated date value.
			this.checkRangeValidity();
			
			// Clear any existing errors on this control
			control.setErrors(null);
		}
	}
	
	private getAdjustedEnteredDateTimesForMinMax(
		control: FormControl<any>,
		field: 'start' | 'end',
		date: Date,
	): Date {
		let utcDate: Date = date;
		
		// If the entered date for the 'start' is below the min range, then set it to
		// the min value. Similarly, if the entered date for the 'end' is after the
		// max value, set it to the max.
		if (field === 'start' && this.minDateISO) {
			const minDate = new Date(this.minDateISO);
			if (utcDate < minDate) {
				utcDate = minDate;
				control.setValue(this.getFormattedDate(this.getTimezoneOffset(utcDate)));
			}
		} else if (field === 'end' && this.maxDateISO) {
			const maxDate = new Date(this.maxDateISO);
			if (utcDate > maxDate) {
				utcDate = maxDate;
				control.setValue(this.getFormattedDate(this.getTimezoneOffset(utcDate)));
			}
		}
		
		return utcDate;
	}
	
	private parseDateTime(input: string): Date | null {
		try {
			const date: Date = parse(input.trim(), this.DATE_DISPLAY_FORMAT, new Date());
			return isValid(date) ? date : null;
		} catch {
			return null;
		}
	}
	
	private getTimezoneOffset(
		date: Date,
		offset: number | null = null
	): Date {
		const appliedOffset: number = offset ?? this.timezoneOffset;
		
		if (!date || appliedOffset === 0) {
			return date;
		}
		
		// Subtract offset to show local time from UTC.
		const adjustedDate = new Date(date);
		adjustedDate.setHours(adjustedDate.getHours() - appliedOffset);
		
		return adjustedDate;
	}
	
	private getFormattedDate(date: Date | null): string {
		if (!date) return '';
		return format(date, this.DATE_DISPLAY_FORMAT);
	}
	
	private convertCalendarDateToUTC(
		calendarDate: Date,
		isStartOfDay: boolean = false
	): Date {
		const localDate: Date = isStartOfDay
			? startOfDay(calendarDate)
			: endOfDay(calendarDate);
		
		return addHours(localDate, this.timezoneOffset);
	}
	
	private checkRangeValidity() {
		if (this.selectedDateRangeUTC?.start && this.selectedDateRangeUTC?.end) {
			const { start, end } = this.selectedDateRangeUTC;
			const difference: number = end.getTime() - start.getTime();
			
			if (difference === 0) {
				this.dateRangeError = `The 'start' & 'end' date must be different.`;
			} else if (difference < 0) {
				this.dateRangeError = `The 'end' date must be after 'start' date.`;
			} else {
				this.dateRangeError = null;
			}
		}
	}
}

export interface DateRangeModel {
	start: Date;
	end: Date;
}

export interface DateRangeISOModel {
	start: string; // ISO UTC datestring
	end: string;   // ISO UTC datestring
}
